import type { ReactNode } from "react";
import { TableRow } from "./TableRow";
import { TableCell } from "./TableCell";
import { SortIcon } from "./SortIcon";

export interface TableColumn<T> {
  key: string;
  label: ReactNode;
  sortable?: boolean;
  render?: (row: T, index: number) => ReactNode;
  className?: string;
  headerClassName?: string;
  // Applied as an inline min-width (px) on both the header and every body cell in
  // this column — use this instead of a dynamically-built `min-w-[${x}px]` Tailwind
  // class, which Tailwind's JIT can't statically extract and so never generates.
  minWidth?: number;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  // Extra className merged onto each body row's TableRow (e.g. to highlight the
  // currently selected row) — TableRow already keeps its own hover/border classes.
  rowClassName?: (row: T, index: number) => string | undefined;
  loading?: boolean;
  loadingText?: ReactNode;
  emptyText?: ReactNode;
  sortCol?: string | null;
  sortDir?: "asc" | "desc";
  onSort?: (key: string) => void;
  className?: string;
}

// Config-driven table shell shared across list pages.
// Sorting is controlled by the caller via sortCol/sortDir/onSort (no internal sort state)
// so pages that sort server-side and pages that sort client-side share the same component.
export const Table = <T extends Record<string, unknown>>({
  columns,
  rows,
  rowKey = (row) => (row as { id?: string | number }).id ?? "",
  onRowClick,
  rowClassName,
  loading = false,
  loadingText = "Loading…",
  emptyText = "No data available",
  sortCol,
  sortDir = "asc",
  onSort,
  className = "",
}: TableProps<T>) => (
  <div className="rounded-xl overflow-x-auto overflow-y-hidden">
    <table
      className={`w-full text-sm ${className}`}
    >
      <thead className="sticky top-0 z-10">
        <TableRow header>
          {columns.map((col) => (
            <TableCell
              key={col.key}
              header
              className={`${col.sortable ? "cursor-pointer select-none" : ""} ${col.headerClassName ?? ""}`}
              style={col.minWidth ? { minWidth: col.minWidth } : undefined}
              onClick={col.sortable ? () => onSort?.(col.key) : undefined}
            >
              <span className="inline-flex items-center whitespace-nowrap">
                {col.label}
                {col.sortable && (
                  <SortIcon active={sortCol === col.key} dir={sortDir} />
                )}
              </span>
            </TableCell>
          ))}
        </TableRow>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-5 py-12 text-center text-gray-400"
            >
              {loadingText}
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-5 py-12 text-center text-gray-400"
            >
              {emptyText}
            </td>
          </tr>
        ) : (
          rows.map((row, idx) => (
            <TableRow
              key={rowKey(row, idx)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={rowClassName?.(row, idx)}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={col.className}
                  style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                >
                  {col.render
                    ? col.render(row, idx)
                    : ((row[col.key] as ReactNode) ?? "—")}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </tbody>
    </table>
  </div>
);
